package com.sweetlime.dungeonamigo.presentation.features.session

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.sweetlime.dungeonamigo.model.Character
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import com.sweetlime.dungeonamigo.presentation.ui.theme.pixelFont
import com.sweetlime.dungeonamigo.presentation.ui.theme.silkFont
import org.koin.compose.koinInject

object SessionLobbyScreen : Screen {
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val vm: SessionViewModel = koinInject()
        val state by vm.sessionState.collectAsState()
        val roomCodeInput by vm.roomCodeInput.collectAsState()
        val characters by vm.availableCharacters.collectAsState()

        var campaignName by remember { mutableStateOf("") }
        var selectedCharacterId by remember { mutableStateOf("") }

        val inSession = state as? SessionState.InSession
        LaunchedEffect(inSession?.room?.id) {
            if (inSession != null) {
                navigator.push(
                    if (inSession.isDm) DmDashboardScreen(inSession.room.id)
                    else PlayerSessionScreen(inSession.room.id)
                )
            }
        }

        Box(
            modifier = Modifier.fillMaxSize().background(BrandColors.Vault),
        ) {
            when (val s = state) {
                is SessionState.Idle -> {
                    LobbyIdleContent(
                        campaignName = campaignName,
                        onCampaignNameChange = { campaignName = it },
                        onCreateRoom = { vm.createRoom(campaignName) },
                        roomCodeInput = roomCodeInput,
                        onRoomCodeChange = vm::onRoomCodeInput,
                        characters = characters,
                        selectedCharacterId = selectedCharacterId,
                        onCharacterSelected = { selectedCharacterId = it },
                        onJoinRoom = {
                            if (selectedCharacterId.isNotBlank()) {
                                vm.joinRoom(roomCodeInput, selectedCharacterId)
                            }
                        },
                    )
                }

                is SessionState.Loading -> {
                    Column(
                        modifier = Modifier.align(Alignment.Center),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(16.dp),
                    ) {
                        CircularProgressIndicator(color = BrandColors.Parchment)
                        Text("Entering the dungeon...", color = BrandColors.IvoryDim, fontSize = 14.sp)
                    }
                }

                is SessionState.InSession -> {
                    Box(Modifier.fillMaxSize().align(Alignment.Center)) {
                        CircularProgressIndicator(color = BrandColors.Parchment, modifier = Modifier.align(Alignment.Center))
                    }
                }

                is SessionState.Error -> {
                    Column(
                        modifier = Modifier.align(Alignment.Center).padding(32.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(16.dp),
                    ) {
                        Text("⚠️", fontSize = 40.sp)
                        Text(s.message, color = BrandColors.Ember, textAlign = TextAlign.Center)
                        Button(
                            onClick = vm::clearError,
                            colors = ButtonDefaults.buttonColors(containerColor = BrandColors.StoneLight),
                        ) {
                            Text("Try Again", color = BrandColors.Ivory)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun LobbyIdleContent(
    campaignName: String,
    onCampaignNameChange: (String) -> Unit,
    onCreateRoom: () -> Unit,
    roomCodeInput: String,
    onRoomCodeChange: (String) -> Unit,
    characters: List<Character>,
    selectedCharacterId: String,
    onCharacterSelected: (String) -> Unit,
    onJoinRoom: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = 18.dp, vertical = 16.dp),
        verticalArrangement = Arrangement.spacedBy(14.dp),
    ) {
        // ── Create Session Card ──────────────────────────────────────────────
        SessionCard {
            EmblemHeader(
                emoji = "⚔️",
                color = BrandColors.Ember,
                title = "Start a Session",
                subtitle = "You're the Dungeon Master",
            )
            Spacer(Modifier.height(14.dp))
            OutlinedTextField(
                value = campaignName,
                onValueChange = onCampaignNameChange,
                placeholder = { Text("Campaign name (optional)", color = BrandColors.IvoryDim, fontSize = 13.sp) },
                modifier = Modifier.fillMaxWidth(),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor   = BrandColors.Ember,
                    unfocusedBorderColor = BrandColors.Slate.copy(alpha = 0.4f),
                    focusedContainerColor   = BrandColors.StoneLight,
                    unfocusedContainerColor = BrandColors.StoneLight,
                    focusedTextColor    = BrandColors.Ivory,
                    unfocusedTextColor  = BrandColors.Ivory,
                    cursorColor         = BrandColors.Parchment,
                ),
                shape = RoundedCornerShape(10.dp),
                singleLine = true,
            )
            Spacer(Modifier.height(10.dp))
            Button(
                onClick = onCreateRoom,
                modifier = Modifier.fillMaxWidth().height(48.dp),
                colors = ButtonDefaults.buttonColors(containerColor = BrandColors.Ember),
                shape = RoundedCornerShape(10.dp),
            ) {
                Text("Create Room", fontWeight = FontWeight.Bold, color = BrandColors.Ivory)
            }
        }

        // ── Join Session Card ────────────────────────────────────────────────
        SessionCard {
            EmblemHeader(
                emoji = "🛡️",
                color = BrandColors.SpellBlue,
                title = "Join a Session",
                subtitle = "Enter the room code from your DM",
            )
            Spacer(Modifier.height(14.dp))
            OutlinedTextField(
                value = roomCodeInput,
                onValueChange = onRoomCodeChange,
                placeholder = {
                    Text(
                        "WOLF",
                        color = BrandColors.IvoryDim,
                        fontSize = 24.sp,
                        fontWeight = FontWeight.Bold,
                        letterSpacing = 10.sp,
                    )
                },
                modifier = Modifier.fillMaxWidth(),
                textStyle = androidx.compose.ui.text.TextStyle(
                    color = BrandColors.Parchment,
                    fontSize = 24.sp,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = 10.sp,
                    textAlign = TextAlign.Center,
                ),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor   = BrandColors.Parchment,
                    unfocusedBorderColor = BrandColors.Slate.copy(alpha = 0.4f),
                    focusedContainerColor   = BrandColors.StoneLight,
                    unfocusedContainerColor = BrandColors.StoneLight,
                    cursorColor = BrandColors.Parchment,
                ),
                shape = RoundedCornerShape(10.dp),
                singleLine = true,
                keyboardOptions = KeyboardOptions(
                    capitalization = KeyboardCapitalization.Characters,
                    keyboardType   = KeyboardType.Text,
                ),
            )
            Spacer(Modifier.height(12.dp))

            if (characters.isEmpty()) {
                Text(
                    "Create a character first to join as a player",
                    color = BrandColors.IvoryDim,
                    fontSize = 12.sp,
                )
            } else {
                CharacterDropdown(
                    characters = characters,
                    selectedId = selectedCharacterId,
                    onSelected = onCharacterSelected,
                )
            }

            Spacer(Modifier.height(10.dp))
            val canJoin = roomCodeInput.length == 4 && selectedCharacterId.isNotBlank()
            Button(
                onClick = onJoinRoom,
                modifier = Modifier.fillMaxWidth().height(48.dp),
                enabled = canJoin,
                colors = ButtonDefaults.buttonColors(
                    containerColor         = BrandColors.Parchment,
                    disabledContainerColor = BrandColors.StoneLight,
                ),
                shape = RoundedCornerShape(10.dp),
            ) {
                Text(
                    "Join Room",
                    fontWeight = FontWeight.Bold,
                    color = if (canJoin) BrandColors.Vault else BrandColors.IvoryDim,
                )
            }
        }

        Text(
            "Sign in required to create or join a room",
            color = BrandColors.IvoryDim,
            fontSize = 11.sp,
            textAlign = TextAlign.Center,
            modifier = Modifier.fillMaxWidth(),
        )
    }
}

/** Card header with a 46dp class-colored emblem badge, title, and subtitle. */
@Composable
private fun EmblemHeader(emoji: String, color: Color, title: String, subtitle: String) {
    val pf = pixelFont()
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        Box(
            modifier = Modifier
                .size(46.dp)
                .clip(RoundedCornerShape(10.dp))
                .background(
                    Brush.verticalGradient(
                        listOf(color.copy(alpha = 0.18f), BrandColors.StoneLight)
                    )
                )
                .border(1.dp, color.copy(alpha = 0.4f), RoundedCornerShape(10.dp)),
            contentAlignment = Alignment.Center,
        ) {
            Text(emoji, fontSize = 22.sp)
        }
        Column {
            Text(title, color = BrandColors.Ivory, fontFamily = pf, fontWeight = FontWeight.SemiBold, fontSize = 16.sp)
            Text(subtitle, color = BrandColors.IvoryDim, fontSize = 12.sp)
        }
    }
}

@Composable
private fun SessionCard(content: @Composable ColumnScope.() -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(14.dp))
            .background(BrandColors.Stone)
            .border(1.dp, BrandColors.Slate.copy(alpha = 0.4f), RoundedCornerShape(14.dp))
            .padding(16.dp),
        content = content,
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun CharacterDropdown(
    characters: List<Character>,
    selectedId: String,
    onSelected: (String) -> Unit,
) {
    var expanded by remember { mutableStateOf(false) }
    val selected = characters.find { it.id == selectedId }

    ExposedDropdownMenuBox(expanded = expanded, onExpandedChange = { expanded = it }) {
        OutlinedTextField(
            value = selected?.let { "${it.name} — ${it.characterClass} ${it.level}" } ?: "Select character",
            onValueChange = {},
            readOnly = true,
            modifier = Modifier.fillMaxWidth().menuAnchor(MenuAnchorType.PrimaryNotEditable),
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor   = BrandColors.Parchment,
                unfocusedBorderColor = BrandColors.Slate.copy(alpha = 0.4f),
                focusedContainerColor   = BrandColors.StoneLight,
                unfocusedContainerColor = BrandColors.StoneLight,
                focusedTextColor     = BrandColors.Ivory,
                unfocusedTextColor   = if (selected != null) BrandColors.Ivory else BrandColors.IvoryDim,
            ),
            shape = RoundedCornerShape(10.dp),
            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded) },
        )
        ExposedDropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            containerColor = BrandColors.Stone,
        ) {
            characters.forEach { char ->
                DropdownMenuItem(
                    text = {
                        Column {
                            Text(char.name, color = BrandColors.Ivory, fontWeight = FontWeight.SemiBold)
                            Text("${char.characterClass} Lv.${char.level}", color = BrandColors.IvoryDim, fontSize = 12.sp)
                        }
                    },
                    onClick = { onSelected(char.id); expanded = false },
                )
            }
        }
    }
}
